import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Plus } from 'lucide-react';
import {
  Badge,
  type BadgeVariant,
  Button,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@beamx/shared';
import { templates, type TemplateStatus } from '../lib/api/templates.js';

const STATUS_VARIANT: Record<TemplateStatus, BadgeVariant> = {
  Published: 'success',
  Draft: 'muted',
  Archived: 'outline',
};

export function TemplatesListPage() {
  const list = useQuery({ queryKey: ['templates'], queryFn: () => templates.list() });

  return (
    <>
      <PageHeader
        title="Templates"
        description="Author transactional and marketing templates with MJML or HTML + Handlebars-style variables."
        actions={
          <Button asChild>
            <Link to="/templates/new">
              <Plus className="h-4 w-4" />
              New template
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : list.data && list.data.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Engine</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-xs text-foreground">{t.templateKey}</TableCell>
                    <TableCell><Badge variant="outline">{t.engine}</Badge></TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[t.status]}>{t.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{t.publishedVersion}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(t.updatedAt).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/templates/${t.id}/edit`}>Edit</Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-6">
              <EmptyState
                icon={FileText}
                title="No templates yet"
                description="Create one via PUT /v1/templates or the editor (coming soon)."
              />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
